import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { KpiTimeChart } from './components/KpiTimeChart';
import { HelpTip } from './components/HelpTip';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { fmtNum, fmtNumShort, fmtMoney, fmtPeriodo, CHART_COLORS } from './components/kpiUtils';
import {
  ShieldBan, Upload, Ban, UserX, CreditCard, AlertTriangle, ArrowUpRight,
  FolderClosed, FileWarning, Landmark, Layers,
} from 'lucide-react';

const TIPO_COLORS = {
  'Usuario': '#6366f1',
  'Cuenta': '#f43f5e',
  'Limite alerta': '#f59e0b',
  'Limite bloqueo retiro': '#0ea5e9',
};

const TABS = [
  { key: 'limites', label: 'En plataforma (límites)' },
  { key: 'alertas', label: 'Por monitoreo Transaccional' },
  { key: 'ros', label: 'Estadísticas de ROS' },
];

const KpisBloqueos = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [tab, setTab] = useState('limites');
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/kpis/bloqueos');
      setData(r.data);
    } catch (e) { toast.error('Error al cargar Bloqueos'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subirArchivo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/kpis/bloqueos/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.total} bloqueos importados`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar el archivo'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const lim = data?.limites || {};
  const alertas = data?.alertas_app || {};
  const ros = data?.ros || {};
  const puede_cargar = data?.puede_cargar;
  const esc = lim.escalado_usuario_cvu || {};

  const tipoSeries = useMemo(() => (lim.tipos || []).map((t, i) => ({
    key: t, name: t, color: TIPO_COLORS[t] || CHART_COLORS[i % CHART_COLORS.length],
  })), [lim.tipos]);

  const mesData = useMemo(() => (lim.por_mes || []).map((m) => {
    const row = { label: fmtPeriodo(m.periodo, true) };
    (lim.tipos || []).forEach((t) => { row[t] = m[t] || 0; });
    return row;
  }), [lim.por_mes, lim.tipos]);

  const tipoBars = useMemo(() => (lim.por_tipo || []).map((t) => ({ label: t.tipo, total: t.total })), [lim.por_tipo]);

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-bloqueos-loading">Cargando bloqueos…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-bloqueos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-rose-500/10"><ShieldBan className="h-6 w-6 text-rose-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Bloqueos</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-bloqueos" /></div>
            <p className="text-sm text-slate-500">Bloqueos por límites de plataforma, por monitoreo transaccional y ROS generados</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="bloqueos" testId="bloqueos-help-btn" />
          <SectionExportButton title="Bloqueos" targetRef={rootRef} testId="bloqueos-export-btn" />
          {puede_cargar && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-rose-600 hover:bg-rose-700 active:scale-95 transition-transform" data-testid="bloqueos-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : 'Subir cuentas bloqueadas (XLSX)'}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={subirArchivo} data-testid="bloqueos-file-input" />
            </>
          )}
        </div>
      </div>

      {/* Segmented tabs (alto contraste) */}
      <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 p-1 bg-slate-100 dark:bg-slate-800 mb-6" data-testid="bloqueos-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-rose-600 text-white shadow' : 'text-slate-600 dark:text-slate-300 hover:text-rose-600'}`}
            data-testid={`bloqueos-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {/* ===== Tab: en plataforma (límites) ===== */}
      {tab === 'limites' && (
        <div data-testid="bloqueos-panel-limites">
          {!lim.cargado ? (
            <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="bloqueos-empty">
              <ShieldBan className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Todavía no hay un reporte de cuentas bloqueadas cargado.</p>
              <p className="text-sm text-slate-400 mt-1">{puede_cargar ? 'Subí el Excel "Reporte Cuentas Bloqueadas" para ver las estadísticas.' : 'Pedile a un administrador que cargue el reporte.'}</p>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard label="Bloqueos totales" value={fmtNum(lim.total)} icon={Ban} accent="#f43f5e" deltaLabel={`${fmtNum(lim.personas)} personas`} testId="bloqueos-kpi-total" />
                <StatCard label="Bloqueo de usuario" value={fmtNum((lim.por_tipo || []).find((t) => t.tipo === 'Usuario')?.total)} icon={UserX} accent="#6366f1" testId="bloqueos-kpi-usuario" />
                <StatCard label="Bloqueo de cuenta (CVU)" value={fmtNum((lim.por_tipo || []).find((t) => t.tipo === 'Cuenta')?.total)} icon={CreditCard} accent="#0ea5e9" testId="bloqueos-kpi-cuenta" />
                <StatCard label="Saldo retenido" value={fmtMoney(lim.monto_retenido)} icon={Landmark} accent="#10b981" deltaLabel="saldo actual de bloqueados" testId="bloqueos-kpi-monto" />
              </div>

              {/* Escalado usuario -> CVU */}
              <Card className="p-5 lg:p-6 mb-6 border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/10" data-testid="bloqueos-escalado">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpRight className="h-5 w-5 text-amber-600" />
                  <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Escalamiento: bloqueo de usuario → bloqueo de CVU</h2>
                  <HelpTip testId="bloqueos-escalado-help" text="De las personas que recibieron un bloqueo de tipo 'Usuario', cuántas además terminaron con un bloqueo de 'Cuenta' (CVU). Se cruza por CUIT / documento." />
                </div>
                <p className="text-sm text-slate-500 mb-4">Cuántas alertas de bloqueo de usuario terminaron escalando a un bloqueo de la cuenta/CVU.</p>
                <div className="flex flex-wrap items-end gap-8">
                  <div><p className="text-3xl font-bold tracking-tighter text-slate-900 dark:text-white">{fmtNum(esc.con_bloqueo_usuario)}</p><p className="text-xs text-slate-500">con bloqueo de usuario</p></div>
                  <div><p className="text-3xl font-bold tracking-tighter text-rose-600">{fmtNum(esc.escalaron_a_cvu)}</p><p className="text-xs text-slate-500">escalaron a bloqueo de CVU</p></div>
                  <div><p className="text-3xl font-bold tracking-tighter text-amber-600">{esc.pct ?? 0}%</p><p className="text-xs text-slate-500">tasa de escalamiento</p></div>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <Card className="p-5 lg:p-6" data-testid="bloqueos-por-tipo">
                  <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Bloqueos por tipo</h2>
                  <KpiTimeChart data={tipoBars} xKey="label" series={[{ key: 'total', name: 'Bloqueos', color: '#f43f5e' }]}
                    type="barras" barLayout="vertical" cellColors={tipoBars.map((t) => TIPO_COLORS[t.label] || '#94a3b8')}
                    height={Math.max(200, tipoBars.length * 46)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="bloqueos-tipo-chart" />
                </Card>
                <Card className="p-5 lg:p-6" data-testid="bloqueos-por-mes">
                  <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Evolución mensual por tipo</h2>
                  <KpiTimeChart data={mesData} series={tipoSeries} type="barras" stacked
                    valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={320} testId="bloqueos-mes-chart" />
                </Card>
              </div>
              <p className="text-xs text-slate-400">Fuente: {lim.archivo || 'Reporte Cuentas Bloqueadas'} · cargado {lim.fecha_carga ? new Date(lim.fecha_carga).toLocaleString('es-AR') : '—'}</p>
            </>
          )}
        </div>
      )}

      {/* ===== Tab: por alertas de la app ===== */}
      {tab === 'alertas' && (
        <div data-testid="bloqueos-panel-alertas">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Casos del monitor" value={fmtNum(alertas.total_casos)} icon={FolderClosed} accent="#4f46e5" testId="bloqueos-kpi-casos" />
            <StatCard label="Casos cerrados" value={fmtNum(alertas.cerrados)} icon={ShieldBan} accent="#f43f5e" deltaLabel="resueltos / bloqueados" testId="bloqueos-kpi-cerrados" />
            <StatCard label="En curso" value={fmtNum(alertas.en_curso)} icon={AlertTriangle} accent="#f59e0b" testId="bloqueos-kpi-encurso" />
            <StatCard label="Casos con ROS" value={fmtNum(alertas.casos_con_ros)} icon={FileWarning} accent="#10b981" testId="bloqueos-kpi-conros" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-5 lg:p-6" data-testid="bloqueos-casos-estado">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Casos por estado</h2>
              <KpiTimeChart data={(alertas.por_estado || []).map((e) => ({ label: e.estado, total: e.total }))} xKey="label"
                series={[{ key: 'total', name: 'Casos', color: '#4f46e5' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(200, (alertas.por_estado || []).length * 44)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="bloqueos-estado-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="bloqueos-cerrados-mes">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Casos cerrados por mes</h2>
              <KpiTimeChart data={(alertas.cerrados_por_mes || []).map((m) => ({ label: fmtPeriodo(m.periodo, true), total: m.total }))}
                series={[{ key: 'total', name: 'Cerrados', color: '#f43f5e' }]} type="barras" valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={300} showLegend={false} testId="bloqueos-cerrados-chart" />
            </Card>
          </div>
          <p className="text-xs text-slate-400 mt-4">Fuente: casos generados por el Monitor Waya dentro de AlertGuard.</p>
        </div>
      )}

      {/* ===== Tab: ROS ===== */}
      {tab === 'ros' && (
        <div data-testid="bloqueos-panel-ros">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="ROS generados" value={fmtNum(ros.total)} icon={FileWarning} accent="#4f46e5" testId="ros-kpi-total" />
            {(ros.por_estado || []).slice(0, 3).map((e, i) => (
              <StatCard key={e.estado} label={`ROS ${e.estado}`} value={fmtNum(e.total)} icon={Layers} accent={CHART_COLORS[(i + 1) % CHART_COLORS.length]} testId={`ros-kpi-${e.estado}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-5 lg:p-6" data-testid="ros-por-estado">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">ROS por estado</h2>
              <KpiTimeChart data={(ros.por_estado || []).map((e) => ({ label: e.estado, total: e.total }))} xKey="label"
                series={[{ key: 'total', name: 'ROS', color: '#4f46e5' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(200, (ros.por_estado || []).length * 44)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="ros-estado-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="ros-por-mes">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">ROS por mes</h2>
              <KpiTimeChart data={(ros.por_mes || []).map((m) => ({ label: fmtPeriodo(m.periodo, true), total: m.total }))}
                series={[{ key: 'total', name: 'ROS', color: '#10b981' }]} type="area" valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={300} showLegend={false} testId="ros-mes-chart" />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default KpisBloqueos;
